/**
 * FinancialReportsPage — three operator-facing reports under one page:
 *
 *   1. Vendor Ledger    — chronological per-vendor with running balance
 *   2. DN Register      — every DN with type + supplier + GST split
 *   3. Cash Forecast    — open bills bucketed by week
 *
 * Each tab has its own filters and works against /accounting/* endpoints
 * built into financial_reports_service.
 */
import { useState } from 'react'
import {
  Alert, Card, Col, DatePicker, Empty, InputNumber, Row, Select, Space,
  Spin, Statistic, Table, Tabs, Tag, Typography,
} from 'antd'
import {
  AccountBookOutlined, AlertOutlined, CalendarOutlined,
  FileExcelOutlined, FundProjectionScreenOutlined, SwapOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const NUM = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

const fmt = (v: string | number | null | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN',
    { minimumFractionDigits: dp, maximumFractionDigits: dp })


// ─────────────────────────────────────────────────────────────────────────
export default function FinancialReportsPage() {
  const { plantId, plants, setPlantId } = usePlantId()

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <FundProjectionScreenOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Financial Reports
      </Title>
      <Text type="secondary">
        Vendor ledger · DN register · Cash forecast — one screen for the
        accountant's daily questions.
      </Text>

      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 16 } }}>
        <Row style={{ marginBottom: 16 }}>
          <Col xs={24} md="auto">
            <Select value={plantId} onChange={setPlantId}
              style={{ width: 280 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="vendor"
          items={[
            { key: 'vendor', label: <span><SwapOutlined /> Vendor Ledger</span>,
              children: <VendorLedgerTab plantId={plantId} /> },
            { key: 'customer', label: <span><SwapOutlined /> Customer Ledger</span>,
              children: <CustomerLedgerTab plantId={plantId} /> },
            { key: 'ar', label: <span><AccountBookOutlined /> AR Aging</span>,
              children: <ARAgingTab plantId={plantId} /> },
            { key: 'dn', label: <span><FileExcelOutlined /> DN Register</span>,
              children: <DNRegisterTab plantId={plantId} /> },
            { key: 'cash', label: <span><CalendarOutlined /> Cash Forecast</span>,
              children: <CashForecastTab plantId={plantId} /> },
          ]}
        />
      </Card>
    </div>
  )
}


// ─── Vendor Ledger ───────────────────────────────────────────────────────
interface SupplierLite { id: string; supplier_name: string; gstin: string | null }
interface LedgerEntry {
  voucher_id: string
  voucher_no: string
  voucher_type: string
  voucher_date: string
  narration: string | null
  bill_ref: string | null
  debit: string
  credit: string
  running_balance: string
  balance_dr_cr: 'DR' | 'CR'
}
interface LedgerResponse {
  vendor_name: string
  vendor_gstin: string | null
  period_from: string | null
  period_to: string
  opening_balance: string
  opening_dr_cr: string
  closing_balance: string
  closing_dr_cr: string
  entries: LedgerEntry[]
}

function VendorLedgerTab({ plantId }: { plantId: string }) {
  const [vendorId, setVendorId] = useState<string | null>(null)
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null])

  const { data: suppliers = [] } = useQuery<SupplierLite[]>({
    queryKey: ['suppliers-lite', plantId],
    queryFn: () => apiClient.get('/masters/suppliers',
      { params: { plant_id: plantId, page_size: 1000 } })
      .then(r => (r.data?.items ?? r.data ?? []).map((s: any) => ({
        id: s.id, supplier_name: s.supplier_name, gstin: s.gstin ?? null,
      }))),
    enabled: !!plantId,
  })

  const { data, isLoading } = useQuery<LedgerResponse>({
    queryKey: ['vendor-ledger', plantId, vendorId,
               range[0]?.format('YYYY-MM-DD'), range[1]?.format('YYYY-MM-DD')],
    queryFn: () => apiClient.get('/accounting/vendor-ledger', {
      params: {
        plant_id: plantId,
        vendor_id: vendorId,
        period_from: range[0]?.format('YYYY-MM-DD'),
        period_to:   range[1]?.format('YYYY-MM-DD'),
      },
    }).then(r => r.data),
    enabled: !!plantId && !!vendorId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <Select
            value={vendorId}
            onChange={setVendorId}
            placeholder="Pick a supplier"
            showSearch
            allowClear
            style={{ width: '100%' }}
            options={suppliers.map(s => ({
              value: s.id,
              label: s.gstin
                ? `${s.supplier_name}  ·  ${s.gstin}`
                : s.supplier_name,
            }))}
            filterOption={(input, opt) =>
              (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Col>
        <Col xs={24} md={10}>
          <DatePicker.RangePicker value={range} onChange={(v) =>
            setRange(v as [Dayjs | null, Dayjs | null] ?? [null, null])}
            style={{ width: '100%' }} />
        </Col>
      </Row>

      {!vendorId && (
        <Empty description="Select a supplier to load their ledger" />
      )}

      {vendorId && isLoading && <Spin />}

      {vendorId && data && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Vendor</Text>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{data.vendor_name}</div>
                {data.vendor_gstin && (
                  <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                    {data.vendor_gstin}
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Opening</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 600 }}>
                  ₹ {fmt(data.opening_balance, 0)} {data.opening_dr_cr}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Entries</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>
                  {data.entries.length}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Closing</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 700,
                  color: data.closing_dr_cr === 'CR' ? '#cf1322' : '#52c41a' }}>
                  ₹ {fmt(data.closing_balance, 0)} {data.closing_dr_cr}
                </div>
              </Card>
            </Col>
          </Row>

          <Table size="small" dataSource={data.entries} rowKey="voucher_id"
            pagination={{ pageSize: 25 }}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Date', dataIndex: 'voucher_date',
                render: (v: string) => dayjs(v).format('DD-MMM-YY') },
              { title: 'Voucher', dataIndex: 'voucher_no',
                render: (v: string, r: LedgerEntry) => (
                  <Space size={4}>
                    <Tag color={
                      r.voucher_type === 'PV' ? 'blue' :
                      r.voucher_type === 'DN' ? 'orange' :
                      r.voucher_type === 'PYV' ? 'green' : 'default'
                    }>{r.voucher_type}</Tag>
                    <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
                  </Space>
                ) },
              { title: 'Bill ref', dataIndex: 'bill_ref',
                render: (v: string | null) => v
                  ? <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
                  : '—' },
              { title: 'Narration', dataIndex: 'narration', ellipsis: true,
                render: (v: string | null) => <Text style={{ fontSize: 11 }}>{v ?? '—'}</Text> },
              { title: 'Debit ₹', dataIndex: 'debit', align: 'right' as const,
                render: (v: string) => Number(v) > 0
                  ? <Text style={{ ...NUM, color: '#52c41a' }}>{fmt(v, 2)}</Text>
                  : <Text type="secondary">—</Text> },
              { title: 'Credit ₹', dataIndex: 'credit', align: 'right' as const,
                render: (v: string) => Number(v) > 0
                  ? <Text style={{ ...NUM, color: '#cf1322' }}>{fmt(v, 2)}</Text>
                  : <Text type="secondary">—</Text> },
              { title: 'Balance ₹', key: 'balance', align: 'right' as const,
                render: (_, r: LedgerEntry) => (
                  <Text strong style={{ ...NUM,
                    color: r.balance_dr_cr === 'CR' ? '#cf1322' : '#52c41a' }}>
                    {fmt(r.running_balance, 2)} {r.balance_dr_cr}
                  </Text>
                ) },
            ]}
          />
        </>
      )}
    </>
  )
}


// ─── DN Register ─────────────────────────────────────────────────────────
interface DNRow {
  voucher_id: string
  voucher_no: string
  voucher_date: string
  dn_type: string
  supplier_id: string | null
  supplier_name: string
  grn_no: string | null
  against_invoice_no: string | null
  taxable: string
  cgst: string
  sgst: string
  igst: string
  total: string
  reason: string | null
}
interface DNResponse {
  rows: DNRow[]
  by_type: { dn_type: string; count: number; total: string }[]
  by_supplier: { supplier_id: string | null; supplier_name: string; dn_count: number; total: string }[]
  grand_total_taxable: string
  grand_total_gst: string
  grand_total: string
}

function DNRegisterTab({ plantId }: { plantId: string }) {
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null])
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined)
  const [supplierFilter, setSupplierFilter] = useState<string | undefined>(undefined)

  const { data: suppliers = [] } = useQuery<SupplierLite[]>({
    queryKey: ['suppliers-lite', plantId],
    queryFn: () => apiClient.get('/masters/suppliers',
      { params: { plant_id: plantId, page_size: 1000 } })
      .then(r => (r.data?.items ?? r.data ?? []).map((s: any) => ({
        id: s.id, supplier_name: s.supplier_name, gstin: s.gstin ?? null,
      }))),
    enabled: !!plantId,
  })

  const { data, isLoading } = useQuery<DNResponse>({
    queryKey: ['dn-register', plantId,
               range[0]?.format('YYYY-MM-DD'), range[1]?.format('YYYY-MM-DD'),
               typeFilter, supplierFilter],
    queryFn: () => apiClient.get('/accounting/dn-register', {
      params: {
        plant_id: plantId,
        period_from: range[0]?.format('YYYY-MM-DD'),
        period_to:   range[1]?.format('YYYY-MM-DD'),
        dn_type: typeFilter,
        supplier_id: supplierFilter,
      },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <DatePicker.RangePicker value={range} onChange={(v) =>
            setRange(v as [Dayjs | null, Dayjs | null] ?? [null, null])}
            style={{ width: '100%' }} />
        </Col>
        <Col xs={24} md={4}>
          <Select value={typeFilter} onChange={setTypeFilter}
            placeholder="All types" allowClear style={{ width: '100%' }}
            options={[
              { value: 'WEIGHT', label: 'Weight' },
              { value: 'QUALITY', label: 'Quality / Sample / Comm.' },
              { value: 'RATE', label: 'Rate' },
            ]} />
        </Col>
        <Col xs={24} md={6}>
          <Select value={supplierFilter} onChange={setSupplierFilter}
            placeholder="All suppliers" allowClear showSearch style={{ width: '100%' }}
            options={suppliers.map(s => ({ value: s.id, label: s.supplier_name }))}
            filterOption={(input, opt) =>
              (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            } />
        </Col>
      </Row>

      {isLoading && <Spin />}
      {!isLoading && data && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={4}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>DNs raised</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>{data.rows.length}</div>
              </Card>
            </Col>
            <Col xs={12} md={5}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Total taxable</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 600 }}>
                  ₹{fmt(data.grand_total_taxable, 0)}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={5}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Total GST reversed</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 600 }}>
                  ₹{fmt(data.grand_total_gst, 0)}
                </div>
              </Card>
            </Col>
            <Col xs={24} md={10}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>By type</Text>
                <Space size={8} wrap>
                  {data.by_type.map(b => (
                    <Tag key={b.dn_type} color={
                      b.dn_type === 'WEIGHT' ? 'blue' :
                      b.dn_type === 'QUALITY' ? 'orange' :
                      b.dn_type === 'RATE' ? 'purple' : 'default'
                    }>
                      {b.dn_type}: {b.count} · ₹{fmt(b.total, 0)}
                    </Tag>
                  ))}
                </Space>
              </Card>
            </Col>
          </Row>

          <Table size="small" dataSource={data.rows} rowKey="voucher_id"
            pagination={{ pageSize: 25 }}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'DN No.', dataIndex: 'voucher_no', width: 130,
                render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
              { title: 'Date', dataIndex: 'voucher_date',
                render: (v: string) => dayjs(v).format('DD-MMM-YY') },
              { title: 'Type', dataIndex: 'dn_type',
                render: (v: string) => <Tag color={
                  v === 'WEIGHT' ? 'blue' : v === 'QUALITY' ? 'orange' :
                  v === 'RATE' ? 'purple' : 'default'
                }>{v}</Tag> },
              { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
              { title: 'Against invoice', dataIndex: 'against_invoice_no',
                render: (v: string | null) => v
                  ? <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
                  : '—' },
              { title: 'Taxable ₹', dataIndex: 'taxable', align: 'right' as const,
                render: (v: string) => <span style={NUM}>{fmt(v, 0)}</span> },
              { title: 'GST ₹', key: 'gst', align: 'right' as const,
                render: (_, r: DNRow) => {
                  const gst = Number(r.cgst) + Number(r.sgst) + Number(r.igst)
                  return <span style={NUM}>{fmt(gst, 0)}</span>
                } },
              { title: 'Total ₹', dataIndex: 'total', align: 'right' as const,
                render: (v: string) => <Text strong style={{ ...NUM, color: '#cf1322' }}>{fmt(v, 0)}</Text> },
            ]}
          />
        </>
      )}
    </>
  )
}


// ─── Cash Forecast ───────────────────────────────────────────────────────
interface ForecastWeek {
  week_start: string
  week_end: string
  bill_count: number
  expected_outflow: string
  overdue: boolean
}
interface ForecastBill {
  pve_id: string
  invoice_no: string
  bill_ref_name: string
  invoice_date: string
  due_date: string
  age_days: number
  over_due_days: number
  supplier_id: string | null
  supplier_name: string
  total_payable: string
  dn_total: string
  paid_total: string
  net_outstanding: string
}
interface ForecastResponse {
  horizon_weeks: number
  overdue_total: string
  horizon_total: string
  weeks: ForecastWeek[]
  bills: ForecastBill[]
}

function CashForecastTab({ plantId }: { plantId: string }) {
  const [horizon, setHorizon] = useState(12)

  const { data, isLoading } = useQuery<ForecastResponse>({
    queryKey: ['cash-forecast', plantId, horizon],
    queryFn: () => apiClient.get('/accounting/cash-forecast', {
      params: { plant_id: plantId, horizon_weeks: horizon },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
        <Col xs={24} md={4}>
          <InputNumber min={1} max={52} value={horizon}
            onChange={(v) => setHorizon(Number(v) || 12)}
            addonBefore="Horizon (weeks)" style={{ width: '100%' }} />
        </Col>
      </Row>

      {isLoading && <Spin />}
      {!isLoading && data && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}
                style={{ borderColor: Number(data.overdue_total) > 0 ? '#ffa39e' : undefined,
                         background: Number(data.overdue_total) > 0 ? '#fff1f0' : undefined }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <AlertOutlined /> Over-due
                </Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700,
                  color: Number(data.overdue_total) > 0 ? '#cf1322' : '#262626' }}>
                  ₹ {fmt(data.overdue_total, 0)}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Next {horizon} weeks</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>
                  ₹ {fmt(data.horizon_total, 0)}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Bills in horizon</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>
                  {data.bills.length}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Weekly buckets */}
          <Card size="small" title="Weekly buckets" style={{ marginBottom: 16 }}>
            <Table size="small" pagination={false} dataSource={data.weeks}
              rowKey="week_start"
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Week', dataIndex: 'week_start',
                  render: (v: string, r: ForecastWeek) => (
                    <Space>
                      <Text style={{ fontSize: 12 }}>
                        {dayjs(v).format('DD MMM')} – {dayjs(r.week_end).format('DD MMM')}
                      </Text>
                      {r.overdue && <Tag color="error">over-due</Tag>}
                    </Space>
                  ) },
                { title: 'Bills', dataIndex: 'bill_count', align: 'right' as const },
                { title: 'Expected outflow ₹', dataIndex: 'expected_outflow',
                  align: 'right' as const,
                  render: (v: string, r: ForecastWeek) => (
                    <Text strong style={{ ...NUM,
                      color: r.overdue ? '#cf1322' : '#262626' }}>
                      ₹ {fmt(v, 0)}
                    </Text>
                  ) },
              ]}
            />
          </Card>

          {/* Detailed bills */}
          <Table size="small" dataSource={data.bills} rowKey="pve_id"
            pagination={{ pageSize: 25 }}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Due', dataIndex: 'due_date',
                render: (v: string, r: ForecastBill) => (
                  <Space>
                    <Text style={{ fontSize: 12,
                      color: r.over_due_days > 0 ? '#cf1322' : '#262626' }}>
                      {dayjs(v).format('DD-MMM-YY')}
                    </Text>
                    {r.over_due_days > 0 && (
                      <Tag color="error">{r.over_due_days}d late</Tag>
                    )}
                  </Space>
                ) },
              { title: 'Bill', dataIndex: 'bill_ref_name',
                render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
              { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
              { title: 'Gross ₹', dataIndex: 'total_payable', align: 'right' as const,
                render: (v: string) => <span style={NUM}>{fmt(v, 0)}</span> },
              { title: '− DN ₹', dataIndex: 'dn_total', align: 'right' as const,
                render: (v: string) => Number(v) > 0
                  ? <Text style={{ ...NUM, color: '#cf1322' }}>− {fmt(v, 0)}</Text>
                  : <Text type="secondary">—</Text> },
              { title: '− Paid ₹', dataIndex: 'paid_total', align: 'right' as const,
                render: (v: string) => Number(v) > 0
                  ? <Text style={{ ...NUM, color: '#52c41a' }}>− {fmt(v, 0)}</Text>
                  : <Text type="secondary">—</Text> },
              { title: 'Net Payable ₹', dataIndex: 'net_outstanding',
                align: 'right' as const,
                render: (v: string) => <Text strong style={{ ...NUM, color: '#52c41a' }}>
                  ₹ {fmt(v, 0)}
                </Text> },
            ]}
          />
        </>
      )}
    </>
  )
}


// ─── Customer Ledger Tab ─────────────────────────────────────────────────
interface CustomerLite { id: string; customer_name: string; gstin: string | null }
interface CustLedgerEntry {
  voucher_id: string
  voucher_no: string
  voucher_type: string
  voucher_date: string
  narration: string | null
  bill_ref: string | null
  debit: string
  credit: string
  running_balance: string
  balance_dr_cr: 'DR' | 'CR'
}
interface CustLedgerResponse {
  customer_name: string
  customer_gstin: string | null
  opening_balance: string
  opening_dr_cr: string
  closing_balance: string
  closing_dr_cr: string
  entries: CustLedgerEntry[]
}

function CustomerLedgerTab({ plantId }: { plantId: string }) {
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null])

  const { data: customers = [] } = useQuery<CustomerLite[]>({
    queryKey: ['customers-lite', plantId],
    queryFn: () => apiClient.get('/sales/customers',
      { params: { plant_id: plantId, page_size: 1000 } })
      .then(r => (r.data?.items ?? r.data ?? []).map((c: any) => ({
        id: c.id, customer_name: c.customer_name, gstin: c.gstin ?? null,
      }))).catch(() => []),
    enabled: !!plantId,
  })

  const { data, isLoading } = useQuery<CustLedgerResponse>({
    queryKey: ['customer-ledger', plantId, customerId,
               range[0]?.format('YYYY-MM-DD'), range[1]?.format('YYYY-MM-DD')],
    queryFn: () => apiClient.get('/accounting/customer-ledger', {
      params: { plant_id: plantId, customer_id: customerId,
                period_from: range[0]?.format('YYYY-MM-DD'),
                period_to:   range[1]?.format('YYYY-MM-DD') },
    }).then(r => r.data),
    enabled: !!plantId && !!customerId,
  })

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <Select value={customerId} onChange={setCustomerId}
            placeholder="Pick a customer" showSearch allowClear
            style={{ width: '100%' }}
            options={customers.map(c => ({ value: c.id,
              label: c.gstin ? `${c.customer_name}  ·  ${c.gstin}` : c.customer_name }))}
            filterOption={(input, opt) =>
              (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())} />
        </Col>
        <Col xs={24} md={10}>
          <DatePicker.RangePicker value={range} onChange={(v) =>
            setRange(v as [Dayjs | null, Dayjs | null] ?? [null, null])}
            style={{ width: '100%' }} />
        </Col>
      </Row>

      {!customerId && <Empty description="Select a customer to load their ledger" />}
      {customerId && isLoading && <Spin />}
      {customerId && data && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Customer</Text>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{data.customer_name}</div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Opening</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 600 }}>
                  ₹ {fmt(data.opening_balance, 0)} {data.opening_dr_cr}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Entries</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>{data.entries.length}</div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Closing</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 700,
                  color: data.closing_dr_cr === 'DR' ? '#cf1322' : '#52c41a' }}>
                  ₹ {fmt(data.closing_balance, 0)} {data.closing_dr_cr}
                </div>
              </Card>
            </Col>
          </Row>
          <Table size="small" dataSource={data.entries} rowKey="voucher_id"
            pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Date', dataIndex: 'voucher_date',
                render: (v: string) => dayjs(v).format('DD-MMM-YY') },
              { title: 'Voucher', dataIndex: 'voucher_no',
                render: (v: string, r: CustLedgerEntry) => (
                  <Space size={4}>
                    <Tag color={
                      r.voucher_type === 'SV' ? 'blue' :
                      r.voucher_type === 'RCV' ? 'green' :
                      r.voucher_type === 'CN' ? 'orange' : 'default'
                    }>{r.voucher_type}</Tag>
                    <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
                  </Space>
                ) },
              { title: 'Narration', dataIndex: 'narration', ellipsis: true,
                render: (v: string | null) => <Text style={{ fontSize: 11 }}>{v ?? '—'}</Text> },
              { title: 'Debit ₹', dataIndex: 'debit', align: 'right' as const,
                render: (v: string) => Number(v) > 0
                  ? <Text style={{ ...NUM, color: '#cf1322' }}>{fmt(v, 2)}</Text> : '—' },
              { title: 'Credit ₹', dataIndex: 'credit', align: 'right' as const,
                render: (v: string) => Number(v) > 0
                  ? <Text style={{ ...NUM, color: '#52c41a' }}>{fmt(v, 2)}</Text> : '—' },
              { title: 'Balance ₹', key: 'balance', align: 'right' as const,
                render: (_, r: CustLedgerEntry) => (
                  <Text strong style={{ ...NUM,
                    color: r.balance_dr_cr === 'DR' ? '#cf1322' : '#52c41a' }}>
                    {fmt(r.running_balance, 2)} {r.balance_dr_cr}
                  </Text>
                ) },
            ]}
          />
        </>
      )}
    </>
  )
}


// ─── AR Aging Tab ────────────────────────────────────────────────────────
interface ARBill {
  invoice_id: string; invoice_no: string; invoice_date: string
  due_date: string | null; age_days: number; over_due_days: number
  aging_bucket: string; customer_name: string
  taxable_value: string; gst_amount: string; total_invoice_value: string
  payment_received: string; outstanding_amount: string
}
interface ARCustSummary {
  customer_id: string; customer_name: string; customer_gstin: string | null
  bill_count: number; oldest_bill_age_days: number
  over_due_count: number; over_due_value: string
  bucket_count: Record<string, number>; bucket_value: Record<string, string>
  total_invoice_value: string; total_received: string; net_outstanding: string
  bills: ARBill[]
}
interface ARResponse {
  grand_total_invoiced: string; grand_total_received: string; grand_net_outstanding: string
  over_due_total_value: string; over_due_total_count: number
  customers: ARCustSummary[]
}

function ARAgingTab({ plantId }: { plantId: string }) {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useQuery<ARResponse>({
    queryKey: ['ar-aging', plantId],
    queryFn: () => apiClient.get('/accounting/ar-aging',
      { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const filtered = data?.customers.filter(c =>
    !search || c.customer_name.toLowerCase().includes(search.toLowerCase())
    || (c.customer_gstin ?? '').toLowerCase().includes(search.toLowerCase())
  ) ?? []

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={10}>
          <input
            placeholder="Filter by customer name or GSTIN"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', border: '1px solid #d9d9d9', borderRadius: 6 }}
          />
        </Col>
      </Row>

      {isLoading && <Spin />}
      {data && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Net outstanding</Text>
                <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>
                  ₹{fmt(data.grand_net_outstanding, 0)}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Total invoiced</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 600 }}>₹{fmt(data.grand_total_invoiced, 0)}</div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>− Received</Text>
                <div style={{ ...NUM, fontSize: 18, color: '#52c41a' }}>− ₹{fmt(data.grand_total_received, 0)}</div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"
                style={{ borderColor: data.over_due_total_count > 0 ? '#ffa39e' : undefined,
                         background: data.over_due_total_count > 0 ? '#fff1f0' : undefined }}>
                <Text type="secondary" style={{ fontSize: 11 }}><AlertOutlined /> Over-due</Text>
                <div style={{ ...NUM, fontSize: 18, fontWeight: 700,
                  color: data.over_due_total_count > 0 ? '#cf1322' : '#262626' }}>
                  {data.over_due_total_count} bills · ₹{fmt(data.over_due_total_value, 0)}
                </div>
              </Card>
            </Col>
          </Row>

          {filtered.length === 0 && <Empty description="No outstanding receivables" />}
          {filtered.map(c => (
            <Card key={c.customer_id} style={{ marginBottom: 12 }}>
              <Row justify="space-between" align="middle" gutter={[12, 12]}>
                <Col flex="auto">
                  <Space size={12} wrap>
                    <UserOutlined style={{ color: '#1677ff' }} />
                    <Text strong style={{ fontSize: 16 }}>{c.customer_name}</Text>
                    <Tag>{c.bill_count} invoice(s)</Tag>
                    {c.over_due_count > 0 && (
                      <Tag color="error">{c.over_due_count} over-due</Tag>
                    )}
                  </Space>
                </Col>
                <Col>
                  <div style={{ ...NUM, fontSize: 22, fontWeight: 700,
                    color: '#52c41a', textAlign: 'right' }}>
                    ₹{fmt(c.net_outstanding, 0)}
                  </div>
                </Col>
              </Row>
              <Table size="small" style={{ marginTop: 12 }}
                dataSource={c.bills} rowKey="invoice_id"
                pagination={c.bills.length > 10 ? { pageSize: 10 } : false}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Invoice', dataIndex: 'invoice_no',
                    render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
                  { title: 'Date', dataIndex: 'invoice_date',
                    render: (v: string) => dayjs(v).format('DD-MMM-YY') },
                  { title: 'Due', dataIndex: 'due_date',
                    render: (v: string | null, r: ARBill) => v
                      ? <Text style={{ fontSize: 12, color: r.over_due_days > 0 ? '#cf1322' : '#262626' }}>
                          {dayjs(v).format('DD-MMM-YY')}
                          {r.over_due_days > 0 && <Tag color="error" style={{ marginLeft: 4 }}>{r.over_due_days}d late</Tag>}
                        </Text>
                      : '—' },
                  { title: 'Age', dataIndex: 'age_days', align: 'right' as const,
                    render: (v: number, r: ARBill) => (
                      <Tag color={r.aging_bucket === '30+' ? 'error' :
                        r.aging_bucket === '15-30' ? 'warning' : 'default'}>{v}d</Tag>) },
                  { title: 'Total ₹', dataIndex: 'total_invoice_value', align: 'right' as const,
                    render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
                  { title: '− Received', dataIndex: 'payment_received', align: 'right' as const,
                    render: (v: string) => Number(v) > 0
                      ? <Text style={{ ...NUM, color: '#52c41a' }}>− ₹{fmt(v, 0)}</Text>
                      : <Text type="secondary">—</Text> },
                  { title: 'Outstanding ₹', dataIndex: 'outstanding_amount', align: 'right' as const,
                    render: (v: string) => <Text strong style={{ ...NUM, color: '#52c41a' }}>
                      ₹{fmt(v, 0)}</Text> },
                ]}
              />
            </Card>
          ))}
        </>
      )}
    </>
  )
}
